<template>
		<div v-if="ro" class="fileZone" :class="clazz" :style="style">
			<div class="fileZoneReadOnlyText">
				<sym-icon name="paper-clip"/>{{bundleDrag}}
			</div>
		</div>
		<div v-else @drop.prevent="onDrop" :data-active="active" 
			@dragenter.prevent="setActive" @dragover.prevent="setActive"
			@dragleave.prevent="setInactive" :class="[clazz,dropClass,'fileZone']" :style="style">
			<div class="zoneContainer" v-show="fileZoneProps.preview && crmFormFiles.length>0">
				<div class="box-file" v-for="crmFormFile in crmFormFiles" :key="crmFormFile.id">
					<close-cross v-if="!ro" @click.prevent="removeFile(crmFormFile.id)" class="corner"></close-cross>
					<img v-if="crmFormFile.picture" :src="thumbnailUrl(crmFormFile, thumbnailSize)" :alt="crmFormFile.fileName" :title="crmFormFile.fileName"/>
					<span v-else class="fi bigIcon" v-html="crmFormFile.icon" :title="crmFormFile.fileName" ></span>
					<div class="box-file-name"><a :href="fileZoneProps.endpoint+'/'+crmFormFile.id" target="_blank">{{limitFileName(crmFormFile)}}</a></div>
				</div>				
			</div>
			<template v-if="uploading">
				<div class="fileZoneText">
					<span v-show="!uploadingFileDone" class="fi uploadingIcon"></span>
					{{uploadingFile.uploadingTitle}}
					<span v-show="uploadingFileDone" class="fi hover-icon"></span>
				</div>
			</template>
			<template v-else>
				<div v-if="active" class="fileZoneText">
					<span>{{env.msg("fileZone.drop")}}</span>
				</div>
				<template v-else >
					<div v-if="fileZoneProps.showDrag" class="fileZoneText" @click.prevent="onFileInput">
						<sym-icon name="paper-clip"/>
						<span>{{bundleDrag}}</span>
					</div>
					<div v-show="fileZoneProps.showDisc || fileZoneProps.showClip || fileZoneProps.showDoc" class="fileZoneButtons">
						<button v-if="fileZoneProps.showDisc" class="sbutton" @click.prevent="onFileInput">
							<sym-icon name="upload"/> <span class="button-text">{{env.msg("fileZone.fromDisc")}}</span>
						</button>
						&nbsp;
						<button v-if="fileZoneProps.showClip" class="sbutton" @click.prevent="onClipboard">
							<sym-icon name="clipboard"/> <span class="button-text">{{env.msg("fileZone.fromClipboard")}}</span>
						</button>
						&nbsp;
						<file-browser-modal v-if="fileZoneProps.showDoc" :button="true" @select-file="onCrmFile"/>
					</div>
				</template>
			</template>
			<input type="file" ref="fileInputId" multiple @change="onInputChange" style="display: none;" />
		</div>
		<div v-if="isError" class="errors">
			<span class="perr">{{errorMessage}}</span>
		</div>
</template>
<script setup lang="ts">
	import { useBaseFile } from './BaseFile';
	import { useCrmFormFile, CrmFormFile } from './CrmFormFile';
	import FileBrowserModal from '@/vue/file/FileBrowserModal.vue';
	import {ref, reactive, computed, onMounted, onUnmounted} from 'vue';
	import CloseCross from "./CloseCross.vue";
	import {injectEnv} from "@fd/Environment";
	import type {FileZone} from "@t/fd/editor/FileZone";
	import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor";
	import SymIcon from "@fd/helpers/SymIcon.vue";
	
	const uprops = defineProps({...editorProps});
	const props = uprops as EditorProps;
	const fileZoneProps = computed<FileZone>(() => props.part as FileZone);
	const {visible, value, clazz, style, ro, errorClass} = useEditor(props);
	
	
	const crmFormFiles = ref<CrmFormFile[]>([])
	
	const env = injectEnv();
	const fileInputId = ref(null);
	const bundleDrag=env.msg(fileZoneProps.value.bundleDrag?fileZoneProps.value.bundleDrag:"fileZone.drag.short");
	const emit = defineEmits(['uploaded']);
	
	const { CLASSEVENTS, dropClass, errorMessage, isError, 
			uploadingFile, uploading, uploadingFileDone,
			active, setActive, setInactive, 
			onDrop, onInputChange, onClipboard, onCrmFile,
			loadZone, removeFromZone } = useBaseFile(fileZoneProps.value, uploadedFile,uploadedAllFiles, endpoint, emit);
	
	const { thumbnailUrl, limitFileName } = useCrmFormFile();
	
	function uploadedFile(data): void {
		//nothing
	}
	
	function endpoint(): string {
		var url = fileZoneProps.value.endpoint || './';
		if (props.part.property) {
			url = env.net.urlParam(url, String(props.part.property ?? ""), value.value);
		}
		return url;
	}
	
	function uploadedAllFiles(): void {
		//loading newly uploaded files
		loadFileZone();
		emit('uploaded');
	}
	
	function loadFileZone() {
		if (fileZoneProps.value.preview) {
			console.log("loading filezone");
			loadZone().then((files)=> {
				crmFormFiles.value=files;
				console.log("crmFormFiles:"+JSON.stringify(crmFormFiles.value));
			});
		}
	}
	
	function removeFile(id: string) {
		console.log("Removing id:"+id);
		const index = crmFormFiles.value.findIndex((item)=>{return item.id===id;});
		if (index>=0) {
			removeFromZone(id)
				.then((result)=> {
						if (result) {
							crmFormFiles.value.splice(index, 1);
						}
				});	
		}
	}
	
	function onFileInput(): void {
		fileInputId.value.click();
	}

	function preventDefaults(e: Event): void {
		e.preventDefault();
	}
		
	onMounted(() => {
		CLASSEVENTS.forEach((eventName) => {
			document.body.addEventListener(eventName, preventDefaults);
		});
		loadFileZone();
	});
	
	onUnmounted(() => {
		CLASSEVENTS.forEach((eventName) => {
			document.body.removeEventListener(eventName, preventDefaults);
		});
	});
</script>
<style>
.zoneContainer {
	display: flex;
	column-gap: 8px;
	flex-flow: row wrap;
	justify-content: space-evenly;
	align-items: center;
	padding: 8px;
}

.box-file {
	max-width: 200px;
	position:relative;
	text-align: center;
	overflow-wrap: break-word;
}

.box-file img {
	max-width:100%;
	max-height:100%;
}

.box-file div {
	max-width:100%;
}
.box-file-name {
	font-size: var(--font-size-s);
}

.bigIcon {
	font-size: 40px;
	padding: 10px;
}

.corner {
	position:absolute;
    top:5px;
    right:5px;
}
</style>
